import { useSearchParams } from 'react-router-dom'
import {
	OrderByType,
	OrderDirectionType,
	ColumnFilterType,
} from '../../pages/Workspace/components/interfaces.table'
import { CustomColumnFilter } from '../../pages/Workspace/components/interfaces.table'
import { useCallback, useMemo } from 'react'

interface TableParams {
	limit: number
	query: string
	offset: number
	orderBy: OrderByType | null
	orderDirection: OrderDirectionType | null
	columnFilters: CustomColumnFilter[]
	restParams?: Record<string, any>
}

const DEFAULT_LIMIT = 10
const DEFAULT_OFFSET = 0

// please don't touch this hook
export const useTableParams = () => {
	const [searchParams, setSearchParams] = useSearchParams()

	const getTableParams = useCallback((): TableParams => {
		const limit = Number(searchParams.get('limit')) || DEFAULT_LIMIT
		const offset = Number(searchParams.get('offset')) || DEFAULT_OFFSET
		const query = searchParams.get('query') || ''
		const orderBy = (searchParams.get('orderBy') as OrderByType) || null
		const orderDirection =
			(searchParams.get('orderDirection') as OrderDirectionType) || null

		// Parsing filters from URL
		const columnFilters: CustomColumnFilter[] = []
		const filterCount = Number(searchParams.get('filterCount')) || 0

		for (let i = 0; i < filterCount; i++) {
			const id = searchParams.get(`filter[${i}].id`)
			const type = Number(
				searchParams.get(`filter[${i}].type`),
			) as ColumnFilterType
			const valueRaw = searchParams.get(`filter[${i}].value`)
			const unit = searchParams.get(`filter[${i}].unit`) || null

			let value: any = valueRaw === 'null' ? '' : valueRaw

			// Handle special case for category/subcategory filters
			if ((id === 'category' || id === 'subcategory') && valueRaw === '') {
				value = 'general_document'
			}

			// Parse JSON or comma-separated values for string values
			if (value && typeof value === 'string') {
				// Try to parse JSON
				if (value.startsWith('[') || value.startsWith('{')) {
					try {
						value = JSON.parse(value)
					} catch (e) {
						// Keep original value if parsing fails
					}
				} else if (value.includes(',')) {
					value = value.split(',')
				}
			}

			// Convert size filter values to bytes if unit is present
			if (id === 'size' && unit) {
				if (typeof value === 'object' && value !== null) {
					value = {
						startValue: value.startValue ? Number(value.startValue) : '',
						endValue: value.endValue ? Number(value.endValue) : '',
					}
				} else if (value) {
					value = Number(value)
				}
			}

			if (id && type !== undefined && value !== null) {
				columnFilters.push({
					id,
					type,
					value,
					unit: unit || undefined,
				})
			}
		}

		const restParams: Record<string, any> = {}
		const restParamsEntries = Array.from(searchParams.entries())
			.filter(
				([key]) =>
					![
						'limit',
						'offset',
						'query',
						'orderBy',
						'orderDirection',
						'filterCount',
					].includes(key) && !key.startsWith('filter['),
			)
			.forEach(([key, value]) => {
				if (value === 'true') restParams[key] = true
				else if (value === 'false') restParams[key] = false
				else restParams[key] = value
			})

		return {
			limit,
			offset,
			query,
			orderBy,
			orderDirection,
			columnFilters,
			restParams: Object.keys(restParams).length > 0 ? restParams : undefined,
		}
	}, [searchParams])

	const setTableParams = useCallback(
		(params: Partial<TableParams>) => {
			// Create stable string for reliable equality checks
			const stableStringifySearchParams = (sp: URLSearchParams): string => {
				const entries = Array.from(sp.entries())
				entries.sort(([aKey, aVal], [bKey, bVal]) =>
					aKey === bKey ? aVal.localeCompare(bVal) : aKey.localeCompare(bKey),
				)
				return entries
					.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
					.join('&')
			}
			setSearchParams((prevSearchParams) => {
				const newParams = new URLSearchParams(prevSearchParams)

				if (params.limit !== undefined)
					newParams.set('limit', params.limit.toString())
				if (params.offset !== undefined)
					newParams.set('offset', params.offset.toString())
				if (params.query !== undefined) {
					if (params.query && params.query.trim()) {
						newParams.set('query', params.query)
					} else {
						newParams.delete('query')
					}
				}
				if (params.orderBy !== undefined) {
					if (params.orderBy) {
						newParams.set('orderBy', params.orderBy)
					} else {
						newParams.delete('orderBy')
					}
				}
				if (params.orderDirection !== undefined) {
					if (params.orderDirection) {
						newParams.set('orderDirection', params.orderDirection)
					} else {
						newParams.delete('orderDirection')
					}
				}

				// Check other params
				if (params.restParams !== undefined) {
					// remove default params
					const existingRestKeys = Array.from(newParams.keys()).filter(
						(key) =>
							![
								'limit',
								'offset',
								'query',
								'orderBy',
								'orderDirection',
								'filterCount',
							].includes(key) && !key.startsWith('filter['),
					)

					existingRestKeys.forEach((key) => newParams.delete(key))

					// add rest params
					Object.entries(params.restParams).forEach(([key, value]) => {
						if (value === true) newParams.set(key, 'true')
						else if (value === false) newParams.set(key, 'false')
						else if (value === null || value === undefined)
							newParams.delete(key)
						else newParams.set(key, String(value))
					})
				}

				// Updating column filters
				if (params.columnFilters !== undefined) {
					// Remove existing filters before adding new ones
					const filterCount = Number(newParams.get('filterCount')) || 0
					for (let i = 0; i < filterCount; i++) {
						newParams.delete(`filter[${i}].id`)
						newParams.delete(`filter[${i}].type`)
						newParams.delete(`filter[${i}].value`)
						newParams.delete(`filter[${i}].unit`)
					}

					// Helper function to convert filter value to string
					const convertFilterValueToString = (
						filter: CustomColumnFilter,
					): string => {
						// Handle arrays
						if (Array.isArray(filter.value)) {
							return filter.value.join(',')
						}

						// Handle objects
						if (typeof filter.value === 'object' && filter.value !== null) {
							try {
								return JSON.stringify(filter.value)
							} catch {
								return String(filter.value)
							}
						}

						// Handle category/subcategory special case
						const isCategoryFilter =
							filter.id === 'category' || filter.id === 'subcategory'
						if (isCategoryFilter && filter.value === 'general_document') {
							return ''
						}

						// Handle regular values
						return filter.value?.toString() || 'null'
					}

					// Add new filters
					params.columnFilters.forEach((filter, index) => {
						const valueStr = convertFilterValueToString(filter)

						newParams.set(`filter[${index}].id`, filter.id)
						newParams.set(`filter[${index}].type`, filter.type.toString())
						newParams.set(`filter[${index}].value`, valueStr)

						// Save unit only for size filter
						if (filter.id === 'size' && filter.unit) {
							newParams.set(`filter[${index}].unit`, filter.unit)
						}
					})
					newParams.set('filterCount', params.columnFilters.length.toString())
				}

				const currentStr = stableStringifySearchParams(prevSearchParams)
				const nextStr = stableStringifySearchParams(newParams)
				return currentStr === nextStr ? prevSearchParams : newParams
			})
		},
		[setSearchParams],
	)

	const memoizedTableParams = useMemo(() => getTableParams(), [getTableParams])

	return useMemo(
		() => ({
			getTableParams: () => memoizedTableParams,
			setTableParams,
		}),
		[memoizedTableParams, setTableParams],
	)
}
